/**
 * @file catalog-form.ts
 * @description Правки вида каталога в панели «Каталог» без отрисовки
 * (phase19.16 Р2а; uiux-design-r2a §2; db-design-r2a, ответы 1, 5; SB65).
 *
 * Перекрёстные правила те же, что у схемы shared (`siteCatalogOptionsSchema`),
 * но кабинет не даёт до них дойти: выбор «Фото слева» сам ставит 2 в ряд на
 * ПК, «Компактная» снимает короткое описание, сортировка по умолчанию всегда
 * среди доступных, последнюю доступную не снять. Своя подпись бейджа
 * записывается, только если прошла `badgeLabelIssue` (иначе — подсказка у
 * поля, а в черновик уходит прежнее значение). Функции не мутируют вход.
 */

import {
  CATALOG_SORT_KEYS,
  PRODUCT_CARD_TEMPLATES,
  type CatalogBadgeKey,
  type CatalogSort,
  type ProductCardTemplate,
  type SiteCatalogOptions,
  badgeLabelIssue,
} from "@chaiberi/shared";

type Grid = SiteCatalogOptions["grid"];

const BUTTON_WORD: Readonly<Record<SiteCatalogOptions["card"]["buyButton"], string>> = {
  text: "кнопка текстом",
  icon: "кнопка значком",
  none: "без кнопки",
};

/** Сводка карточки для элемента «Товары»: «Классика» · бейдж, фасовка, рейтинг, кнопка текстом. */
export function cardSummary(options: SiteCatalogOptions): string {
  const card = options.card;
  const label = PRODUCT_CARD_TEMPLATES.find((t) => t.key === card.template)?.label ?? card.template;
  const fields = [card.fields.badge ? "бейдж" : null, card.fields.skuPicker ? "фасовка" : null, card.fields.rating ? "рейтинг" : null, card.fields.shortDescription ? "описание" : null].filter(Boolean);
  return [`«${label}»`, [...fields, BUTTON_WORD[card.buyButton]].join(", ")].join(" · ");
}

/** Сколько в ряд на ПК можно у шаблона карточки («Фото слева» — не больше 2). */
export function maxDesktopColumns(template: ProductCardTemplate): Grid["desktop"] {
  return template === "horizontal" ? 2 : 4;
}

/**
 * Что шаблон карточки поменял сам («Фото слева» — 2 в ряд, «Компактная» —
 * без описания): прежние значения, чтобы вернуть их при смене шаблона
 * обратно (приёмка Р2а). Живёт в форме панели, в черновик не уходит.
 */
export interface CardTemplateMemory {
  desktop?: Grid["desktop"];
  shortDescription?: boolean;
}

/**
 * Сменить шаблон карточки и привести связанные поля в порядок; то, что
 * пришлось поменять, запоминается и возвращается, когда шаблон это снова
 * позволяет.
 */
export function applyCardTemplate(
  options: SiteCatalogOptions,
  template: ProductCardTemplate,
  memory: CardTemplateMemory = {},
): { options: SiteCatalogOptions; memory: CardTemplateMemory } {
  const next: CardTemplateMemory = { ...memory };
  const max = maxDesktopColumns(template);
  let desktop = options.grid.desktop;
  if (desktop > max) {
    next.desktop = desktop;
    desktop = max;
  } else if (next.desktop !== undefined && next.desktop <= max) {
    desktop = next.desktop;
    delete next.desktop;
  }
  let shortDescription = options.card.fields.shortDescription;
  if (template === "compact") {
    if (shortDescription) next.shortDescription = true;
    shortDescription = false;
  } else if (next.shortDescription !== undefined) {
    shortDescription = next.shortDescription;
    delete next.shortDescription;
  }
  return {
    options: { ...options, card: { ...options.card, template, fields: { ...options.card.fields, shortDescription } }, grid: { ...options.grid, desktop } },
    memory: next,
  };
}

/** Подсказка о ширине карточек на ПК (uiux-design-r2a §2: 4 в ряд при фильтрах справа). */
export function gridHint(options: SiteCatalogOptions): string | null {
  if (options.grid.desktop === 4 && options.card.template !== "compact") {
    return "Карточки будут узкими (~210 px): справа фильтры. Лучше 3 в ряд или карточка «Компактная».";
  }
  return null;
}

/** Включить или снять сортировку в списке покупателя; последнюю не снять, умолчание — переезжает. */
export function toggleSort(options: SiteCatalogOptions, sort: CatalogSort, on: boolean): SiteCatalogOptions {
  const current = options.sorts.enabled;
  if (on) {
    if (current.includes(sort)) return options;
    // Порядок — как в справочнике сортировок.
    const enabled = CATALOG_SORT_KEYS.filter((k) => k === sort || current.includes(k));
    return { ...options, sorts: { ...options.sorts, enabled } };
  }
  const enabled = current.filter((k) => k !== sort);
  if (enabled.length === 0) return options;
  const fallback = enabled[0] ?? options.sorts.default;
  return { ...options, sorts: { default: options.sorts.default === sort ? fallback : options.sorts.default, enabled } };
}

/** Сортировка по умолчанию; не включённая — включается. */
export function setDefaultSort(options: SiteCatalogOptions, sort: CatalogSort): SiteCatalogOptions {
  const withSort = toggleSort(options, sort, true);
  return { ...withSort, sorts: { ...withSort.sorts, default: sort } };
}

/**
 * Своя подпись бейджа: пустая — подпись платформы (ключ убирается),
 * допустимая — записывается; с ошибкой — `null` (в черновик не уходит).
 */
export function setBadgeLabel(options: SiteCatalogOptions, key: CatalogBadgeKey, label: string): SiteCatalogOptions | null {
  const labels = { ...options.badgeLabels };
  if (label.trim() === "") {
    delete labels[key];
    return { ...options, badgeLabels: labels };
  }
  if (badgeLabelIssue(label)) return null;
  labels[key] = label;
  return { ...options, badgeLabels: labels };
}
